import React, { useState, useEffect, useMemo } from 'react';
import { Note, MagnifyingGlass, FloppyDisk, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * OfficeNotesTab — staff-only notepad inside Manage Entries.
 *
 * Two notepads:
 *  1. Show-wide note — one free-text area for the whole show
 *  2. Per-rider notes — one note per unique rider_name pulled from entries
 *
 * Backend: GET/PUT /api/shows/{id}/office-notes
 * Riders never see these notes. Visible only to producer + master_admin.
 *
 * Auto-save: each field uses a 1.5s debounce — no Save buttons cluttering
 * the UI. A small ✓ indicator confirms the last save.
 */
export const OfficeNotesTab = ({ showId, entries }) => {
  const [loading, setLoading] = useState(true);
  const [showNote, setShowNote] = useState('');
  const [riderNotes, setRiderNotes] = useState({});
  const [riderSearch, setRiderSearch] = useState('');
  const [savingShow, setSavingShow] = useState(false);
  const [savingRider, setSavingRider] = useState({});
  const [savedShowAt, setSavedShowAt] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/shows/${showId}/office-notes`);
        if (cancel) return;
        setShowNote(data.show_note || '');
        setRiderNotes(data.rider_notes || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load notes');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [showId]);

  // Unique rider names from entries — same source the entries table uses, so
  // rider counts always line up with what the producer sees in the table.
  const riders = useMemo(() => {
    const map = new Map();
    for (const e of entries || []) {
      const name = (e.rider_name || '').trim();
      if (!name) continue;
      map.set(name, (map.get(name) || 0) + 1);
    }
    // Include any rider that already has a note even if they have no current
    // entries — useful for sticky notes from prior years.
    for (const name of Object.keys(riderNotes)) {
      if (!map.has(name) && (riderNotes[name] || '').trim()) map.set(name, 0);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries, riderNotes]);

  const filteredRiders = useMemo(() => {
    const q = riderSearch.trim().toLowerCase();
    if (!q) return riders;
    return riders.filter(r => r.name.toLowerCase().includes(q));
  }, [riders, riderSearch]);

  // Debounced auto-save for the show-wide note.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(async () => {
      setSavingShow(true);
      try {
        await api.put(`/shows/${showId}/office-notes`, { show_note: showNote });
        setSavedShowAt(Date.now());
      } catch {
        /* swallow — toast would be annoying on every keystroke */
      } finally {
        setSavingShow(false);
      }
    }, 1500);
    return () => clearTimeout(t);
    // We deliberately exclude `loading` from deps after init — only re-run on edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNote]);

  const saveRiderNote = async (riderName, note) => {
    setSavingRider(s => ({ ...s, [riderName]: true }));
    try {
      await api.put(`/shows/${showId}/office-notes`, {
        rider_note: { rider_name: riderName, note },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to save note for ${riderName}`);
    } finally {
      setSavingRider(s => ({ ...s, [riderName]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="office-notes-tab">
      <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Note size={18} className="text-[#FF9500]" /> Show-Wide Notes
          </h3>
          <span className="text-[10px] text-[#9E9E9E] flex items-center gap-1" data-testid="show-note-save-state">
            {savingShow ? 'Saving…' : (savedShowAt ? <><Check size={10} className="text-[#34C759]" /> Saved</> : '')}
          </span>
        </div>
        <p className="text-[11px] text-[#9E9E9E] mb-3">
          Shared notepad for everyone managing this show. Riders never see this. Auto-saves as you type.
        </p>
        <textarea
          value={showNote}
          onChange={(e) => setShowNote(e.target.value)}
          rows={6}
          placeholder="Office reminders, deadlines, special accommodations, payment-followup list…"
          className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500] resize-y"
          data-testid="show-note-textarea"
        />
      </div>

      <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <Note size={18} className="text-[#0096FF]" /> Per-Rider Notes
            <span className="text-xs text-[#9E9E9E] font-normal">{riders.length} rider{riders.length === 1 ? '' : 's'}</span>
          </h3>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              value={riderSearch}
              onChange={(e) => setRiderSearch(e.target.value)}
              placeholder="Search riders…"
              className="pl-7 pr-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF] w-56"
              data-testid="rider-notes-search"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#9E9E9E] mb-3">
          Each rider gets a sticky note that follows them across all their entries in this show. Auto-saves on field blur.
        </p>

        {loading ? (
          <p className="text-[#9E9E9E] text-sm">Loading…</p>
        ) : filteredRiders.length === 0 ? (
          <p className="text-[#555] text-sm italic">
            {riderSearch ? 'No riders match that search.' : 'No riders entered yet.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredRiders.map(({ name, count }) => (
              <RiderNoteRow
                key={name}
                name={name}
                count={count}
                value={riderNotes[name] || ''}
                onCommit={(note) => {
                  setRiderNotes(rn => ({ ...rn, [name]: note }));
                  saveRiderNote(name, note);
                }}
                saving={!!savingRider[name]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RiderNoteRow = ({ name, count, value, onCommit, saving }) => {
  const [local, setLocal] = useState(value);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => { setLocal(value); }, [value]);

  const handleBlur = () => {
    const next = (local || '').trim();
    if (next === (value || '').trim()) return;
    onCommit(next);
    setSavedAt(Date.now());
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#262626] rounded p-3" data-testid={`rider-note-${name}`}>
      <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-bold text-white truncate">{name}</span>
          {count > 0 && (
            <span className="text-[10px] text-[#9E9E9E] bg-[#161616] px-1.5 py-0.5 rounded">
              {count} {count === 1 ? 'entry' : 'entries'}
            </span>
          )}
          {count === 0 && (
            <span className="text-[10px] text-[#555] italic">No entries this show</span>
          )}
        </div>
        <span className="text-[10px] text-[#9E9E9E] flex items-center gap-1">
          {saving ? <><FloppyDisk size={10} /> Saving…</> : (savedAt ? <><Check size={10} className="text-[#34C759]" /> Saved</> : '')}
        </span>
      </div>
      <textarea
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        rows={2}
        placeholder="Outstanding balance, dietary needs, contact info, payment plan…"
        className="w-full px-2 py-1.5 bg-[#161616] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF] resize-y"
        data-testid={`rider-note-textarea-${name}`}
      />
    </div>
  );
};

export default OfficeNotesTab;
